// Copyright (c) 2026 Goffy. All Rights Reserved.

export type ResolvePostGameSelectionRouteInput = {
  roundId: string;
  debug: boolean;
  tourId?: string | null;
  selectedGameDefinitionIds: ReadonlyArray<string>;
  gameDefinitionTypes: Record<string, string>;
  hasMysteryGame: boolean;
  source: string;
};

export type ResolvedRoundRoute = {
  path: string;
  query: string;
};

export function resolvePostGameSelectionRoute(input: ResolvePostGameSelectionRouteInput): ResolvedRoundRoute {
  const { roundId, debug, tourId, selectedGameDefinitionIds, gameDefinitionTypes, hasMysteryGame, source } = input;
  const hasTeamGame = selectedGameDefinitionIds.some(
    (gameId) => (gameDefinitionTypes[gameId] ?? '').trim().toLowerCase() === 'team'
  );
  const goToRoundStart = source !== 'roundstart-open' && (hasTeamGame || hasMysteryGame);
  const path = goToRoundStart ? `/round/${roundId}/roundstart` : `/round/${roundId}/hole/1/compact`;

  const params = new URLSearchParams();
  if (debug) {
    params.set('debug', 'true');
  }
  if (tourId) {
    params.set('tourId', tourId);
  }
  // HoleScreen seeds its hazard/game checkboxes from this param on mount, before its one-shot
  // GET /api/rounds/{roundId} fallback resolves — without it, that fetch race against the
  // round.update command's own persistence is the only source, and a fast enough client (Quick
  // Round's auto-start has near-zero elapsed time) can reach hole 1 before the backend has
  // caught up, landing with no games attached until something else triggers a re-render.
  if (!goToRoundStart && selectedGameDefinitionIds.length > 0) {
    params.set('gameDefinitionIds', selectedGameDefinitionIds.join(','));
  }

  return {
    path,
    query: params.toString(),
  };
}

export function buildRoundRouteHref(route: ResolvedRoundRoute): string {
  return route.query ? `${route.path}?${route.query}` : route.path;
}
